import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Search, ArrowRight } from 'lucide-react';
import { countiesByState } from '../data/mock';

const normalize = (s) => s.toLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss');

export const allCounties = Object.entries(countiesByState).flatMap(([bundesland, list]) =>
  (list || []).filter((c) => c.status === 'live' || c.status === 'beta').map((c) => ({ name: c.name, bundesland }))
);

export const LandkreisSearch = ({ onSubmit, buttonLabel = 'Bewertung starten', size = 'lg', testIdPrefix = 'hero' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);

  const matches = useMemo(() => {
    const q = normalize(query.trim());
    if (q.length < 2) return [];
    return allCounties
      .filter((c) => normalize(c.name).includes(q) || normalize(c.bundesland).includes(q))
      .sort((a, b) => Number(!normalize(a.name).startsWith(q)) - Number(!normalize(b.name).startsWith(q)) || a.name.localeCompare(b.name, 'de'))
      .slice(0, 8);
  }, [query]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (county) => {
    setSelected(county);
    setQuery(county.name);
    setOpen(false);
  };

  const submit = () => {
    const county = selected && selected.name === query ? selected : matches[0] || null;
    onSubmit(county ? { landkreis: county.name, bundesland: county.bundesland } : null, query.trim());
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && matches[active]) choose(matches[active]); else submit(); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const big = size === 'lg';

  return (
    <div ref={wrapRef} className="hv-search relative w-full" data-testid={`${testIdPrefix}-landkreis-search`}>
      <div className={`flex flex-col sm:flex-row gap-3 ${big ? 'p-2 bg-[#FFFEFA] border border-[#DEDFD3] rounded-[6px] shadow-[0_18px_50px_-24px_rgba(21,59,43,0.45)]' : ''}`}>
        <div className="relative flex-1">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#647064] pointer-events-none" aria-hidden="true" />
          <input
            type="text"
            role="combobox"
            aria-expanded={open && matches.length > 0}
            aria-controls={`${testIdPrefix}-landkreis-listbox`}
            aria-autocomplete="list"
            aria-label="Landkreis eingeben"
            data-testid={`${testIdPrefix}-landkreis-input`}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); setOpen(true); setActive(0); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Ihr Landkreis, z. B. Freising"
            autoComplete="off"
            className={`hv-search-input w-full ${big ? 'h-[60px] text-lg' : 'h-[52px] text-base'} pl-12 pr-4 bg-[#FFFEFA] border ${big ? 'border-transparent' : 'border-[#DEDFD3]'} rounded-[4px] text-[#19392B] placeholder:text-[#8A938A] focus:outline-none focus:ring-2 focus:ring-[#1C432F]`}
          />
          {open && matches.length > 0 && (
            <ul
              id={`${testIdPrefix}-landkreis-listbox`}
              role="listbox"
              data-testid={`${testIdPrefix}-landkreis-options`}
              className="hv-search-list absolute left-0 right-0 top-[calc(100%+8px)] z-40 max-h-80 overflow-y-auto bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] shadow-[0_12px_32px_rgba(25,57,43,0.12)] text-left"
            >
              {matches.map((c, i) => (
                <li
                  key={`${c.bundesland}-${c.name}`}
                  role="option"
                  aria-selected={i === active}
                  data-testid={`${testIdPrefix}-landkreis-option-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); choose(c); }}
                  onMouseEnter={() => setActive(i)}
                  className={`flex items-baseline justify-between gap-4 px-4 py-3 cursor-pointer ${i === active ? 'bg-[#EEE9DC]' : ''}`}
                >
                  <span className="text-[#19392B] font-medium">{c.name}</span>
                  <span className="text-sm text-[#647064] whitespace-nowrap">{c.bundesland}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={submit}
          data-testid={`${testIdPrefix}-landkreis-submit`}
          className={`hv-btn-primary inline-flex items-center justify-center gap-2 ${big ? 'h-[60px] px-7 text-base' : 'h-[52px] px-6 text-[15px]'} bg-[#1C432F] hover:bg-[#153B2B] text-[#F8F6F0] font-semibold rounded-[4px]`}
        >
          {big ? <Search className="w-5 h-5" aria-hidden="true" /> : null}
          <span>{buttonLabel}</span>
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default LandkreisSearch;
